import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PortalHeader } from "@/pages/client/_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function ClientMessages() {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get("/portal/messages").then((r) => setMessages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/portal/messages", { body });
      setMessages((m) => [...m, data]); setBody("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="fv-fade-up flex flex-col h-[calc(100vh-8rem)]">
      <PortalHeader title="Messages" subtitle="Chat directly with your Fairview advisor." />

      <div className="flex-1 overflow-y-auto rounded-2xl border border-white/8 bg-[#0f0f11] p-5 space-y-3" data-testid="client-messages-thread">
        {messages.length === 0 ? (
          <p className="text-center text-[#71717A] py-10">No messages yet. Say hello to your advisor.</p>
        ) : messages.map((m) => (
          <div key={m.message_id} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
              m.sender === "client" ? "fv-gold-gradient text-[#0A0A0B]" : "bg-[#1C1C1F] text-[#D4D4D8] border border-white/8"}`}>
              {m.body}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="flex gap-2 mt-4" data-testid="client-message-form">
        <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your message…"
          data-testid="client-message-input" className="bg-[#121214] border-white/10 text-white h-11" />
        <Button type="submit" disabled={sending || !body.trim()} data-testid="client-message-send"
          className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg h-11 px-5 shrink-0"><Send className="w-4 h-4" /></Button>
      </form>
    </div>
  );
}
